import { useEffect, useRef } from 'react'

/**
 * Fire `save(value)` after the user stops editing for `delay` ms.
 *
 * Guarantees:
 *   - Skips the initial mount so we don't POST the empty starting value.
 *   - A pending debounced save flushes on component unmount (so switching
 *     tabs, navigating away, or submitting an encounter never silently
 *     drops the last keystrokes).
 *   - Returns a `flush()` imperative so callers (e.g. "Submit & lock")
 *     can force-commit before they run the terminal action.
 */
export function useAutosave(value, save, delay = 800) {
  const valueRef = useRef(value)
  const savedRef = useRef(value) // last value we committed to save()
  const saveRef = useRef(save)
  const timerRef = useRef(null)
  const firstRef = useRef(true)

  // Keep refs pointed at the latest props so the cleanup closure doesn't
  // capture stale values.
  useEffect(() => { valueRef.current = value }, [value])
  useEffect(() => { saveRef.current = save }, [save])

  // Debounced scheduling on every value change.
  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false
      savedRef.current = value
      return
    }
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      commitIfDirty()
    }, delay)
    return () => {
      // Do nothing here; the unmount cleanup below handles pending saves.
      // We specifically DON'T clear the timer on every re-render because
      // React will re-run this effect on every value change and that would
      // cancel the save that's about to fire.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delay])

  // Flush on unmount: if a debounced save is pending, commit now.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      commitIfDirty()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function commitIfDirty() {
    if (valueRef.current !== savedRef.current) {
      savedRef.current = valueRef.current
      try {
        saveRef.current(valueRef.current)
      } catch {
        // Save failures are surfaced by the caller's mutation state; we
        // don't rethrow out of a cleanup.
      }
    }
  }

  // Imperative flush for callers that want to force-commit (e.g. before
  // submitting an encounter). Safe to call any number of times.
  function flush() {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    commitIfDirty()
  }

  return flush
}
